import React, { useEffect, useCallback } from 'react';
import { Loader2, LucideIcon, ChevronLeft, ChevronRight, X, Inbox } from 'lucide-react';
import { STATUS_WON, PAGE_SIZE_OPTIONS } from '../../hooks/utils';

// ============================================
// Stats Card
// ============================================
interface StatsCardProps {
  icon: LucideIcon;
  title: string;
  value: React.ReactNode;
  subtitle?: string;
  color?: 'cyan' | 'green' | 'orange' | 'purple' | 'blue' | 'emerald' | 'red';
  trend?: number;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  icon: Icon,
  title,
  value,
  subtitle,
  color = 'cyan',
  trend,
}) => (
  <div className="stats-card">
    <div className="stats-card-header">
      <div className={`stats-icon ${color}`}>
        <Icon size={16} />
      </div>
      {trend !== undefined && (
        <span className={`trend ${trend > 0 ? 'up' : 'down'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="stats-value">{value}</div>
    <div className="stats-title">{title}</div>
    {subtitle && <div className="stats-subtitle">{subtitle}</div>}
  </div>
);

// ============================================
// Loading Spinner
// ============================================
interface LoadingSpinnerProps {
  text?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  text = 'Ачаалж байна...' 
}) => (
  <div className="loading-container">
    <Loader2 className="spinner" size={40} />
    <p>{text}</p>
  </div>
);

// ============================================
// Alert Badge
// ============================================
interface AlertBadgeProps {
  type: 'critical' | 'warning' | 'info';
  count: number;
}

export const AlertBadge: React.FC<AlertBadgeProps> = ({ type, count }) => {
  const colors = {
    critical: 'red',
    warning: 'orange',
    info: 'blue',
  };
  return <span className={`alert-badge ${colors[type]}`}>{count}</span>;
};

// ============================================
// Status Badge
// ============================================
interface StatusBadgeProps {
  status: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const isWon = status === STATUS_WON;
  return (
    <span className={`status-badge ${isWon ? 'won' : 'lost'}`}>
      {status}
    </span>
  );
};

// ============================================
// Anomaly Badge
// ============================================
interface AnomalyBadgeProps {
  type: string;
}

export const AnomalyBadge: React.FC<AnomalyBadgeProps> = ({ type }) => {
  const isHigh = type === 'Төсөвтэй бараг тэнцүү' || type === 'Төсвөөс хэтэрсэн';
  return (
    <span className={`anomaly-badge ${isHigh ? 'high' : 'low'}`}>
      {type}
    </span>
  );
};

// ============================================
// Empty State
// ============================================
interface EmptyStateProps {
  message: string;
  icon?: LucideIcon;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  message,
  icon: Icon
}) => (
  <div className="empty-state">
    {Icon && <Icon size={48} />}
    <p>{message}</p>
  </div>
);

// ============================================
// Empty Panel (bd-card variant)
// ============================================
export const EmptyPanel: React.FC<{ text?: string }> = ({ text = 'Мэдээлэл олдсонгүй' }) => (
  <div className="bd-empty-state">
    <Inbox size={28} />
    <span>{text}</span>
  </div>
);

// ============================================
// Error State
// ============================================
interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ message, onRetry }) => (
  <div className="error-state">
    <p>{message}</p>
    {onRetry && (
      <button onClick={onRetry} className="retry-btn">
        Дахин оролдох
      </button>
    )}
  </div>
);

// ============================================
// Detail Error (wraps ErrorState with status extraction)
// ============================================
interface DetailErrorProps {
  error: unknown;
  notFoundMessage: string;
  onRetry?: () => void;
}

export const DetailError: React.FC<DetailErrorProps> = ({ error, notFoundMessage, onRetry }) => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return (
    <ErrorState
      message={status === 404 ? notFoundMessage : 'Сервертэй холбогдоход алдаа гарлаа'}
      onRetry={onRetry}
    />
  );
};

// ============================================
// Error Banner (inline error bar)
// ============================================
interface ErrorBannerProps {
  message?: string;
  onRetry?: () => void;
}

export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  message = 'Өгөгдөл ачаалахад алдаа гарлаа.',
  onRetry,
}) => (
  <div className="error-banner">
    <span>{message}</span>
    {onRetry && <button type="button" onClick={onRetry} className="error-banner-retry">Дахин оролдох</button>}
  </div>
);

// ============================================
// Page Header
// ============================================
interface PageHeaderProps {
  icon?: LucideIcon;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  subtitle,
  actions,
}) => (
  <div className="page-header-bar">
    <div className="page-header-bar-left">
      <h1 className="page-title">
        {Icon && <Icon size={24} />} {title}
      </h1>
      {subtitle && <p className="page-subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="page-header-bar-actions">{actions}</div>}
  </div>
);

// ============================================
// Page Shell — Unified Facebook-style page wrapper
// ============================================
interface PageShellProps {
  icon?: LucideIcon;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  breadcrumb?: React.ReactNode;
  variant?: 'default' | 'full' | 'centered' | 'feed';
  className?: string;
  children: React.ReactNode;
}

export const PageShell: React.FC<PageShellProps> = ({
  icon,
  title,
  subtitle,
  actions,
  breadcrumb,
  variant = 'default',
  className,
  children,
}) => (
  <div className={`page-shell page-shell--${variant}${className ? ' ' + className : ''}`}>
    {breadcrumb && <div className="page-shell-breadcrumb">{breadcrumb}</div>}
    {(title || actions) && (
      <PageHeader icon={icon} title={title ?? ''} subtitle={subtitle} actions={actions} />
    )}
    <div className="page-shell-body">{children}</div>
  </div>
);

// ============================================
// Pagination (bd-pagination)
// ============================================
interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizes?: readonly number[];
  /** Тусгай мэдээлэл (default: "{page}/{totalPages}") */
  info?: React.ReactNode;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizes = PAGE_SIZE_OPTIONS,
  info,
}) => (
  <div className="bd-pagination">
    {onPageSizeChange && pageSize != null && (
      <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))} className="bd-select sm">
        {pageSizes.map((n) => <option key={n} value={n}>{n}/хуудас</option>)}
      </select>
    )}
    <span className="bd-page-info">{info ?? `${page}/${totalPages}`}</span>
    <div className="bd-page-btns">
      <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        <ChevronLeft size={14} />
      </button>
      <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
        <ChevronRight size={14} />
      </button>
    </div>
  </div>
);

// ============================================
// Mini Pager (bd-network-pager)
// ============================================
interface MiniPagerProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const MiniPager: React.FC<MiniPagerProps> = ({
  page,
  totalPages,
  onPageChange,
}) => (
  <div className="bd-network-pager">
    <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
      <ChevronLeft size={13} />
    </button>
    <span>{page}/{totalPages}</span>
    <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
      <ChevronRight size={13} />
    </button>
  </div>
);

// ============================================
// Info Modal
// ============================================
interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose, title, children }) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="info-modal-overlay" onClick={onClose}>
      <div className="info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="info-modal-header">
          <h2>{title}</h2>
          <button className="info-modal-close" onClick={onClose} aria-label="Хаах">
            <X size={20} />
          </button>
        </div>
        <div className="info-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Role Badge
// ============================================
interface RoleBadgeProps {
  role: string;
  onClick?: () => void;
}

export const RoleBadge: React.FC<RoleBadgeProps> = ({ role, onClick }) => {
  const colorClass = role === 'admin' ? 'role-admin' : role === 'vip' ? 'role-vip' : 'role-normal';
  return (
    <span className={`role-badge ${colorClass}`} onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
      {role}
    </span>
  );
};

// ============================================
// Form Input (shared)
// ============================================
interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export const FormInput: React.FC<FormInputProps> = ({ label, ...props }) => (
  <div className="form-field">
    {label && <label className="form-label">{label}</label>}
    <input {...props} className={`form-input ${props.className || ''}`} />
  </div>
);

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  children: React.ReactNode;
}

export const FormSelect: React.FC<FormSelectProps> = ({ label, children, ...props }) => (
  <div className="form-field">
    {label && <label className="form-label">{label}</label>}
    <select {...props} className={`form-select ${props.className || ''}`}>
      {children}
    </select>
  </div>
);
